import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Sparkles, Users, LineChart, CheckCircle2, BarChart4, Shield, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { SecurityCompliance } from '@/components/home/SecurityCompliance';

const About = () => {
  const fadeInUpItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>About The PEO App | Our Mission & Solution</title>
        <meta name="description" content="Learn how The PEO App is transforming the PEO shopping experience for businesses with our streamlined application process and vetted provider network." />
        <link rel="canonical" href="https://thepeoapp.com/about" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/10 to-background py-16 md:py-24">
          <div className="absolute inset-0 w-full h-full">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80 mb-6 tracking-tight">
                About The PEO App
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                We're on a mission to transform how businesses find and connect with PEOs through innovative technology and transparent processes.
              </p>
              <Link to="/">
                <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                  Get Free PEO Quotes Now
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-4 pt-16">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
          <motion.section 
            className="bg-white rounded-xl p-8 shadow-lg border border-primary/10 hover:shadow-xl transition-all duration-300"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
            variants={fadeInUpItem}
          >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">The Problem</h2>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Small businesses, startups, and non-profits struggle to access the full benefits of PEOs because the shopping process is broken. Every PEO requires lengthy, complex applications, forcing companies to waste time, repeat work, navigate confusing pricing, and expose their sensitive data to potential risk.
              </p>
            </motion.section>

          <motion.section 
            className="bg-white rounded-xl p-8 shadow-lg border border-primary/10 hover:shadow-xl transition-all duration-300"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            variants={fadeInUpItem}
          >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <LineChart className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">The Solution</h2>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                With years of leadership experience in HR tech and PEO, our team has seen firsthand how the right PEO can be a game-changer - if clients can access it easily. We eliminate the friction with a single, centralized application that connects to multiple PEOs, delivering quotes in a matter of days.
              </p>
            </motion.section>
          </div>

          <motion.section 
            className="max-w-5xl mx-auto bg-white rounded-xl p-10 shadow-lg border border-primary/10 mb-16 hover:shadow-xl transition-all duration-300"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.3 }}
            variants={fadeInUpItem}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Our Commitment</h2>
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              We're committed to providing transparent, unbiased PEO recommendations. Our patent pending technology ensures you get matched with the best-fit PEOs for your specific needs, while our streamlined process saves you time and reduces complexity.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              <div className="bg-primary/5 p-6 rounded-lg">
                <CheckCircle2 className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Transparent Process</h3>
                <p className="text-muted-foreground">We provide clear information about pricing, services, and provider capabilities at every step.</p>
              </div>
              
              <div className="bg-primary/5 p-6 rounded-lg">
                <BarChart4 className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Data-Driven Matching</h3>
                <p className="text-muted-foreground">Our algorithm uses your specific business needs to find your perfect PEO match.</p>
              </div>
              
              <div className="bg-primary/5 p-6 rounded-lg">
                <Shield className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Vetted Providers</h3>
                <p className="text-muted-foreground">Every PEO in our network has been thoroughly vetted for service quality and reliability.</p>
              </div>
            </div>
          </motion.section>
          
          {/* Security Compliance Section */}
          <SecurityCompliance />
          
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.5 }}
            variants={fadeInUpItem}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Ready to Find Your Perfect PEO Partner?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Join the hundreds of businesses that have already streamlined their PEO shopping experience through our platform.
            </p>
            <Link to="/">
              <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                Get Free PEO Quotes Now
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
